import React from "react";
import { Check, X, Trash2, Clock } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";

/* ===================================================== */
/* Helpers                                               */
/* ===================================================== */

const getStatusStyles = (status) => {
  switch (status) {
    case "active":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "invited":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "rejected":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-gray-50 text-gray-600 border-gray-200";
  }
};

const formatStatus = (status) => {
  if (!status) return "Unknown";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

/* ===================================================== */
/* Component                                             */
/* ===================================================== */

export default function ContractorCard({
  contractor,
  variant, // incoming | outgoing | active
  onAccept,
  onReject,
  onCancel,
  onDelete,
  loadingId,
}) {
  const { activeCompany } = useCompany();

  const isLoading = loadingId === contractor._id;

  /* ---------------------------------------------------- */
  /* Resolve Companies                                    */
  /* ---------------------------------------------------- */

  const isClient =
    String(contractor.clientCompanyId) ===
    String(activeCompany?._id);

  const otherCompany = isClient
    ? contractor.contractorCompany || {
        name: contractor.contractorCompanyId,
      }
    : contractor.clientCompany || {
        name: contractor.clientCompanyId,
      };

  const companyName =
    otherCompany?.name ||
    otherCompany?.legalName ||
    "Unknown Company";

  /* ---------------------------------------------------- */
  /* UI                                                   */
  /* ---------------------------------------------------- */

  return (
    <div className="border rounded-xl p-4 bg-white flex items-center justify-between shadow-sm">

      {/* LEFT */}
      <div className="flex flex-col gap-1 min-w-0">

        {/* Company Name */}
        <div className="font-medium text-gray-900 truncate">
          {companyName}
        </div>

        {/* Role */}
        <div className="text-xs text-gray-500">
          {isClient ? "Contractor" : "Client"}
        </div>

        {/* Status */}
        <div
          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border w-fit ${getStatusStyles(
            contractor.status
          )}`}
        >
          {contractor.status === "invited" && (
            <Clock className="w-3 h-3" />
          )}
          {formatStatus(contractor.status)}
        </div>

      </div>

      {/* RIGHT ACTIONS */}
      <div className="flex items-center gap-2 ml-4">

        {/* INCOMING */}
        {variant === "incoming" && (
          <>
            <button
              onClick={() => onAccept?.(contractor)}
              disabled={isLoading}
              className="px-3 py-1.5 text-sm rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
            >
              <Check className="w-4 h-4" />
              Accept
            </button>

            <button
              onClick={() => onReject?.(contractor)}
              disabled={isLoading}
              className="px-3 py-1.5 text-sm rounded-md border text-rose-600 hover:bg-rose-50 disabled:opacity-50 flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Reject
            </button>
          </>
        )}

        {/* OUTGOING */}
        {variant === "outgoing" && (
          <button
            onClick={() => onCancel?.(contractor)}
            disabled={isLoading}
            className="px-3 py-1.5 text-sm rounded-md border text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel Invite
          </button>
        )}

        {/* ACTIVE */}
        {variant === "active" && (
          <button
            onClick={() => onDelete?.(contractor)}
            disabled={isLoading}
            className="px-3 py-1.5 text-sm rounded-md border text-rose-600 hover:bg-rose-50 disabled:opacity-50 flex items-center gap-1"
          >
            <Trash2 className="w-4 h-4" />
            Remove
          </button>
        )}

      </div>
    </div>
  );
}